import { UnknownTimeZoneError } from './agency-day.js';
import { parts, toLocalDate } from './local-date.js';
const formatters = new Map();
function formatterFor(timeZone) {
    const cached = formatters.get(timeZone);
    if (cached)
        return cached;
    let formatter;
    try {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        });
    }
    catch {
        throw new UnknownTimeZoneError(timeZone);
    }
    formatters.set(timeZone, formatter);
    return formatter;
}
function wallClock(instant, timeZone) {
    const fields = Object.fromEntries(formatterFor(timeZone)
        .formatToParts(instant)
        .map((part) => [part.type, part.value]));
    return {
        date: toLocalDate(`${fields.year}-${fields.month}-${fields.day}`),
        minute: Number(fields.hour) * 60 + Number(fields.minute),
    };
}
/** The local date and minute of `instant` in `timeZone`. Seconds are dropped. */
export function localTimeOf(instant, timeZone) {
    return wallClock(instant, timeZone);
}
/** The wall clock at `instant`, read as if it were UTC, in ms. */
function wallAsUtc(instant, timeZone) {
    const { date, minute } = wallClock(instant, timeZone);
    const { year, month, day } = parts(date);
    return Date.UTC(year, month - 1, day, 0, minute);
}
/** Milliseconds the zone is ahead of UTC at `instant` (negative west of Greenwich). */
function offsetAt(instant, timeZone) {
    // The wall clock has no seconds, so compare at minute resolution.
    return wallAsUtc(instant, timeZone) - Math.floor(instant.getTime() / 60_000) * 60_000;
}
const SIX_HOURS = 6 * 3_600_000;
/**
 * The instant a local date and minute name in `timeZone`.
 *
 * Two edges, both on DST days, decided rather than left to chance — the same
 * way `Temporal`'s "compatible" disambiguation decides them:
 *
 *  - **A repeated time** (autumn, 01:30 happens twice): the earlier instant.
 *  - **A skipped time** (spring, 02:30 does not happen in New York): moved
 *    forward by the gap, to 03:30. A visit cannot start in an hour that does
 *    not exist, and forward keeps it on the day it was booked for.
 *
 * Offsets either side of a transition are read six hours away from it; no zone
 * changes offset twice in twelve hours.
 *
 * `minute` may be 1440, which is midnight at the end of `date`.
 */
export function instantOf(date, minute, timeZone) {
    const { year, month, day } = parts(date);
    const wall = Date.UTC(year, month - 1, day, 0, minute);
    const before = wall - offsetAt(new Date(wall - SIX_HOURS), timeZone);
    const after = wall - offsetAt(new Date(wall + SIX_HOURS), timeZone);
    const real = [before, after].filter((candidate) => wallAsUtc(new Date(candidate), timeZone) === wall);
    // Both real: a repeated time, take the earlier. Neither: a skipped time, and
    // the offset from before the transition carries it forward past the gap.
    return new Date(real.length > 0 ? Math.min(...real) : before);
}
//# sourceMappingURL=local-time.js.map